(function (root) {
  'use strict';

  /*
   * Kabeldimensjon for DC, to-leder (pluss + minus), kobber.
   *
   *   A  = 2 × L × I × ρ / ΔU          (mm²)
   *   ΔU = U × p / 100                 (V, tillatt spenningsfall)
   *   I  = P / U                       (A, når effekt er oppgitt)
   *
   * L er kabellengden én vei; faktoren 2 tar med både pluss- og minuskabel (tur og retur).
   *
   * ρ = 0,0175 Ω·mm²/m: kobber ved 20 °C (samme grunnlag som Victron, Blue Sea og ABYC-formelen).
   *   Varm plassering (motorrom, varmt rom, rør eller kabelkanal): +20 %, ρ = 0,021. Det tilsvarer
   *   kobber ved ca. 70 °C (temperaturkoeffisient 0,0039 per °C × 50 °C ≈ 0,2).
   *
   * Strømføringsevne: ISO 13297 tabell A1, kolonnen for 70 °C-isolasjon, kobberleder, bunt med
   *   inntil 3 ledere (tabelltittel i BoatHowTo sin gjengivelse; ISO 13297 erstattet ISO 10133, samme tall). Varm plassering: × 0,75, som er
   *   ISO-faktoren for motorrom med 70 °C-isolasjon.
   *   Margin: kabelen må tåle minst 1,25 × strømmen (sammenhengende belastning). Anbefalt størrelse
   *   er den største av kravet til spenningsfall og kravet til strømføringsevne.
   *
   * AWG: minste vanlige AWG-størrelse som oppfyller de samme to kravene. Strømføringsevnen for
   *   AWG-tverrsnittene er interpolert (log-log) mellom radene i samme ISO-tabell.
   */
  var COPPER_RESISTIVITY_20C = 0.0175;
  var WARM_RESISTANCE_FACTOR = 1.2;
  var STANDARD_CABLE_SIZES_MM2 = Object.freeze([0.75, 1, 1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120]);
  var AMPACITY_A_70C = Object.freeze({
    '0.75': 10,
    '1': 14,
    '1.5': 18,
    '2.5': 25,
    '4': 35,
    '6': 45,
    '10': 65,
    '16': 90,
    '25': 120,
    '35': 160,
    '50': 210,
    '70': 265,
    '95': 310,
    '120': 360
  });
  var AWG_SIZES = Object.freeze([
    { awg: '18', area: 0.823 },
    { awg: '16', area: 1.31 },
    { awg: '14', area: 2.08 },
    { awg: '12', area: 3.31 },
    { awg: '10', area: 5.26 },
    { awg: '8', area: 8.37 },
    { awg: '6', area: 13.3 },
    { awg: '4', area: 21.2 },
    { awg: '2', area: 33.6 },
    { awg: '1', area: 42.4 },
    { awg: '1/0', area: 53.5 },
    { awg: '2/0', area: 67.4 },
    { awg: '3/0', area: 85 },
    { awg: '4/0', area: 107.2 }
  ]);
  var HOT_SPACE_FACTOR = 0.75;
  var AMPACITY_MARGIN = 1.25;
  var HEAVY_CABLE_MM2 = 50;
  var DEFAULT_DROP_PERCENT = 3;
  var LIMITS = Object.freeze({
    voltage: [6, 60],
    power: [1, 100000],
    current: [0.1, 2000],
    length: [0.1, 200],
    drop: [1, 10]
  });
  // Toleranse for flyttall, slik at f.eks. nøyaktig 25,000 mm² gir 25 mm² og ikke 35 mm².
  var EPS = 1e-9;

  // Tolker tall som folk skriver dem. Norsk/svensk: komma er desimaltegn, og punktum foran
  // nøyaktig tre sifre er tusenskille («2.000» = 2000). Engelsk: omvendt. Alt annet er ugyldig.
  function toFiniteNumber(value, decimalComma) {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
    var text = String(value).replace(/[\s  ]+/g, '');
    if (text === '') return null;
    var useComma = decimalComma !== false;
    var group = useComma ? '.' : ',';
    var decimal = useComma ? ',' : '.';
    var groupPattern = useComma ? /^[1-9]\d{0,2}(\.\d{3})+(,\d+)?$/ : /^[1-9]\d{0,2}(,\d{3})+(\.\d+)?$/;
    if (groupPattern.test(text)) text = text.split(group).join('');
    text = text.split(decimal).join('.');
    if (!/^\d+(\.\d+)?$|^\.\d+$/.test(text)) return NaN;
    var number = Number(text);
    return Number.isFinite(number) ? number : NaN;
  }

  function resistivityFor(hot) {
    return hot ? COPPER_RESISTIVITY_20C * WARM_RESISTANCE_FACTOR : COPPER_RESISTIVITY_20C;
  }

  // Tabellverdi for standardstørrelser, log-log-interpolert mellom radene for andre tverrsnitt (AWG).
  function ampacityForArea(area, hot) {
    var sizes = STANDARD_CABLE_SIZES_MM2;
    var base;
    if (area <= sizes[0]) {
      base = AMPACITY_A_70C[String(sizes[0])] * (area / sizes[0]);
    } else if (area >= sizes[sizes.length - 1]) {
      base = AMPACITY_A_70C[String(sizes[sizes.length - 1])];
    } else {
      for (var i = 1; i < sizes.length; i += 1) {
        if (area <= sizes[i]) {
          var a0 = sizes[i - 1];
          var a1 = sizes[i];
          var i0 = AMPACITY_A_70C[String(a0)];
          var i1 = AMPACITY_A_70C[String(a1)];
          var t = Math.log(area / a0) / Math.log(a1 / a0);
          base = Math.exp(Math.log(i0) + t * Math.log(i1 / i0));
          break;
        }
      }
    }
    return hot ? base * HOT_SPACE_FACTOR : base;
  }

  function checkRange(value, range) {
    if (value === null) return 'missing';
    if (Number.isNaN(value)) return 'invalid';
    if (value < range[0] || value > range[1]) return 'range';
    return null;
  }

  function awgFor(requiredArea, requiredCurrent, hot) {
    for (var i = 0; i < AWG_SIZES.length; i += 1) {
      var size = AWG_SIZES[i];
      if (size.area >= requiredArea * (1 - EPS) && ampacityForArea(size.area, hot) >= requiredCurrent * (1 - EPS)) {
        return { awg: size.awg, area: size.area };
      }
    }
    return null;
  }

  function calculateCableSize(input) {
    input = input || {};

    var mode = input.mode === 'current' ? 'current' : 'power';
    var comma = input.decimalComma !== false;
    var voltage = toFiniteNumber(input.voltage, comma);
    var amount = toFiniteNumber(input.value, comma);
    var length = toFiniteNumber(input.length, comma);
    var dropPercent = toFiniteNumber(input.dropPercent == null ? DEFAULT_DROP_PERCENT : input.dropPercent, comma);
    var hot = input.hot === true;

    var errors = {};
    var voltageError = checkRange(voltage, LIMITS.voltage);
    var amountError = checkRange(amount, mode === 'power' ? LIMITS.power : LIMITS.current);
    var lengthError = checkRange(length, LIMITS.length);
    if (voltageError) errors.voltage = voltageError;
    if (amountError) errors.value = amountError;
    if (lengthError) errors.length = lengthError;
    if (checkRange(dropPercent, LIMITS.drop)) errors.drop = 'invalid';

    var current = null;
    var power = null;
    if (!voltageError && !amountError) {
      current = mode === 'power' ? amount / voltage : amount;
      power = mode === 'power' ? amount : amount * voltage;
    }

    if (Object.keys(errors).length) {
      return { valid: false, errors: errors, mode: mode, voltage: voltage, current: current, power: power };
    }

    var resistivity = resistivityFor(hot);
    var allowedDrop = voltage * (dropPercent / 100);
    var requiredArea = (2 * length * current * resistivity) / allowedDrop;
    var requiredCurrent = current * AMPACITY_MARGIN;

    var dropSize = null;
    var recommended = null;
    for (var i = 0; i < STANDARD_CABLE_SIZES_MM2.length; i += 1) {
      var size = STANDARD_CABLE_SIZES_MM2[i];
      var meetsDrop = size >= requiredArea * (1 - EPS);
      if (meetsDrop && dropSize === null) dropSize = size;
      if (meetsDrop && ampacityForArea(size, hot) >= requiredCurrent * (1 - EPS)) {
        recommended = size;
        break;
      }
    }

    var result = {
      valid: true,
      errors: {},
      mode: mode,
      voltage: voltage,
      current: current,
      power: power,
      length: length,
      roundTripLength: length * 2,
      dropPercent: dropPercent,
      allowedDrop: allowedDrop,
      hot: hot,
      resistivity: resistivity,
      requiredArea: requiredArea,
      requiredCurrent: requiredCurrent,
      dropSize: dropSize,
      recommendedSize: recommended,
      tooLarge: recommended === null,
      tooLargeReason: null,
      governedBy: null,
      actualDrop: null,
      actualDropPercent: null,
      ampacity: null,
      awg: null
    };

    if (recommended === null) {
      var maxSize = STANDARD_CABLE_SIZES_MM2[STANDARD_CABLE_SIZES_MM2.length - 1];
      result.tooLargeReason = requiredArea > maxSize ? 'drop' : 'current';
      return result;
    }

    result.governedBy = dropSize !== null && recommended > dropSize ? 'ampacity' : 'drop';
    // Grov kabel bare på grunn av lengden: vis at kortere kabel eller høyere spenning hjelper.
    result.heavyByDrop = result.governedBy === 'drop' && recommended >= HEAVY_CABLE_MM2;
    result.actualDrop = (2 * length * current * resistivity) / recommended;
    result.actualDropPercent = (result.actualDrop / voltage) * 100;
    result.ampacity = ampacityForArea(recommended, hot);
    result.awg = awgFor(requiredArea, requiredCurrent, hot);
    return result;
  }

  // Samme effekt på høyere standardspenning: forslag når kabelen blir for grov.
  function higherVoltageAlternatives(input) {
    var voltage = toFiniteNumber(input.voltage, input.decimalComma !== false);
    if (input.mode === 'current' || !voltage) return [];
    return [24, 48]
      .filter(function (v) { return v > voltage; })
      .map(function (v) {
        var alt = calculateCableSize({
          mode: 'power',
          voltage: v,
          value: input.value,
          length: input.length,
          dropPercent: input.dropPercent,
          hot: input.hot,
          decimalComma: input.decimalComma
        });
        return alt.valid && !alt.tooLarge ? { voltage: v, current: alt.current, size: alt.recommendedSize } : null;
      })
      .filter(Boolean);
  }

  var api = Object.freeze({
    COPPER_RESISTIVITY_20C: COPPER_RESISTIVITY_20C,
    WARM_RESISTANCE_FACTOR: WARM_RESISTANCE_FACTOR,
    STANDARD_CABLE_SIZES_MM2: STANDARD_CABLE_SIZES_MM2,
    AMPACITY_A_70C: AMPACITY_A_70C,
    AWG_SIZES: AWG_SIZES,
    HOT_SPACE_FACTOR: HOT_SPACE_FACTOR,
    AMPACITY_MARGIN: AMPACITY_MARGIN,
    LIMITS: LIMITS,
    ampacityForArea: ampacityForArea,
    calculateCableSize: calculateCableSize,
    higherVoltageAlternatives: higherVoltageAlternatives,
    toFiniteNumber: toFiniteNumber
  });

  root.PVCableSizeCalculator = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (
    typeof HTMLElement === 'undefined' ||
    typeof customElements === 'undefined' ||
    customElements.get('pv-cable-size-calculator')
  ) {
    return;
  }

  function readJson(element, selector) {
    var node = element.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch (_error) {
      return {};
    }
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  class PVCableSizeCalculatorElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-calculator-form]');
      if (!this.form) return;

      this.t = readJson(this, '[data-i18n]');
      this.productMap = readJson(this, '[data-product-map]');
      this.locale = this.dataset.locale || 'nb-NO';
      this.decimalComma = !/^en/i.test(this.locale);
      this.live = this.querySelector('[data-live]');
      this.echo = this.querySelector('[data-echo]');
      this.customVoltageWrap = this.querySelector('[data-custom-voltage]');
      this.valueInput = this.form.elements.namedItem('value');
      this.valueLabel = this.querySelector('[data-value-label]');
      this.valueUnit = this.querySelector('[data-value-unit]');
      this.valueHelp = this.querySelector('[data-value-help]');
      this.derived = this.querySelector('[data-derived]');
      this.touched = {};
      this.lastAnnouncement = '';

      var element = this;
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        element.touched = { voltage: true, value: true, length: true };
        element.update(true);
      });
      this.form.addEventListener('input', function () {
        element.update(false);
      });
      this.form.addEventListener('change', function (event) {
        var target = event.target;
        if (target && target.name === 'mode') element.switchMode(target.value);
        if (target && target.name) element.touched[target.name === 'voltage_custom' ? 'voltage' : target.name] = true;
        element.update(false);
      });
      this.form.addEventListener('focusout', function (event) {
        var target = event.target;
        if (!target || !target.name) return;
        var key = target.name === 'voltage_custom' ? 'voltage' : target.name;
        if (target.value !== '') element.touched[key] = true;
        element.update(false);
      });

      this.mode = this.form.elements.namedItem('mode').value || 'power';
      this.applyModeText();
      this.update(false);
    }

    selectedVoltage() {
      var choice = this.form.elements.namedItem('voltage').value;
      var isCustom = choice === 'custom';
      if (this.customVoltageWrap) this.customVoltageWrap.hidden = !isCustom;
      return isCustom ? this.form.elements.namedItem('voltage_custom').value : choice;
    }

    readInput() {
      var elements = this.form.elements;
      return {
        mode: elements.namedItem('mode').value,
        voltage: this.selectedVoltage(),
        value: elements.namedItem('value').value,
        length: elements.namedItem('length').value,
        dropPercent: elements.namedItem('drop').value,
        hot: elements.namedItem('hot').checked,
        decimalComma: this.decimalComma
      };
    }

    // Valgt knapp markeres med klasse i tillegg til :has(), så det virker i eldre nettlesere.
    markChecked() {
      var radios = this.form.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i += 1) {
        var label = radios[i].closest('label');
        if (label) label.classList.toggle('is-checked', radios[i].checked);
      }
    }

    switchMode(newMode) {
      if (newMode === this.mode) return;
      // Behold det brukeren har skrevet: regn om watt til ampere eller motsatt.
      var before = calculateCableSize(Object.assign(this.readInput(), { mode: this.mode }));
      this.mode = newMode;
      if (before.current != null && before.power != null && this.valueInput.value !== '') {
        var converted = newMode === 'current' ? before.current : before.power;
        this.valueInput.value = this.formatInputNumber(converted);
      } else {
        // Uten gyldig spenning kan tallet ikke regnes om; 1000 W skal ikke stå igjen som 1000 A.
        this.valueInput.value = '';
      }
      this.applyModeText();
    }

    applyModeText() {
      var isPower = this.mode !== 'current';
      if (this.valueLabel) this.valueLabel.textContent = isPower ? this.t.powerLabel : this.t.currentLabel;
      if (this.valueUnit) this.valueUnit.textContent = isPower ? 'W' : 'A';
      if (this.valueHelp) this.valueHelp.textContent = isPower ? this.t.powerHelp : this.t.currentHelp;
      this.valueInput.setAttribute('placeholder', isPower ? this.t.powerPlaceholder : this.t.currentPlaceholder);
    }

    update(announceNow) {
      var input = this.readInput();
      var result = calculateCableSize(input);
      this.markChecked();
      this.renderDerived(result);
      this.renderErrors(result.errors);
      this.renderResult(result, input);
      this.scheduleAnnouncement(announceNow);
    }

    renderDerived(result) {
      if (!this.derived) return;
      if (result.current == null || result.power == null) {
        this.derived.textContent = '';
        return;
      }
      var text =
        this.mode === 'current'
          ? fill(this.t.derivedPower, { power: this.formatNumber(result.power, 0), voltage: this.formatNumber(result.voltage, 1) })
          : fill(this.t.derivedCurrent, { current: this.formatNumber(result.current, 1), voltage: this.formatNumber(result.voltage, 1) });
      this.derived.textContent = text;
    }

    renderErrors(errors) {
      var fields = ['voltage', 'value', 'length'];
      for (var i = 0; i < fields.length; i += 1) {
        var field = fields[i];
        var code = errors[field];
        // Tomme felt gir ingen feilmelding; resultatpanelet sier hva som mangler.
        var show = Boolean(this.touched[field] && (code === 'invalid' || code === 'range'));
        var node = this.querySelector('[data-error-for="' + field + '"]');
        var input =
          field === 'voltage'
            ? this.form.elements.namedItem('voltage_custom')
            : this.form.elements.namedItem(field);
        if (!node || !input) continue;
        if (show) {
          node.textContent = this.errorText(field, code);
          node.hidden = false;
          input.setAttribute('aria-invalid', 'true');
        } else {
          node.textContent = '';
          node.hidden = true;
          input.removeAttribute('aria-invalid');
        }
      }
    }

    errorText(field) {
      if (field === 'voltage') return this.t.errorVoltage;
      if (field === 'length') return this.t.errorLength;
      return this.mode === 'current' ? this.t.errorCurrent : this.t.errorPower;
    }

    setOut(name, text) {
      var node = this.querySelector('[data-out="' + name + '"]');
      if (node) node.textContent = text;
    }

    alternativesText(input) {
      var alternatives = higherVoltageAlternatives(input);
      return alternatives
        .map(
          function (alt) {
            return fill(this.t.alternative, {
              voltage: alt.voltage,
              current: this.formatNumber(alt.current, 1),
              size: this.formatSize(alt.size)
            });
          }.bind(this)
        )
        .concat(
          alternatives.some(function (alt) { return alt.voltage === 48; }) && this.t.alternative48 ? [this.t.alternative48] : []
        )
        .join(' ');
    }

    renderResult(result, input) {
      var dash = '-';
      var empty = this.querySelector('[data-state="empty"]');
      var filled = this.querySelector('[data-state="result"]');
      var warning = this.querySelector('[data-warning]');
      var note = this.querySelector('[data-note]');
      var link = this.querySelector('[data-product-link]');
      var linkNote = this.querySelector('[data-product-note]');
      var contact = this.querySelector('[data-contact-link]');
      var dropLabel = this.querySelector('[data-drop-label]');
      var requiredLabel = this.querySelector('[data-required-label]');

      this.announcement = '';
      if (requiredLabel) {
        requiredLabel.textContent = fill(this.t.requiredLabel, {
          percent: this.formatNumber(toFiniteNumber(input.dropPercent, this.decimalComma) || DEFAULT_DROP_PERCENT, 1)
        });
      }

      if (!result.valid) {
        if (empty) empty.hidden = false;
        if (filled) filled.setAttribute('data-inactive', '');
        this.setOut('size', dash);
        this.setOut('awg', dash);
        this.setOut('current', result.current != null ? this.formatNumber(result.current, 1) + '\u00a0A' : dash);
        this.setOut('required', dash);
        this.setOut('drop', dash);
        this.setOut('ampacity', dash);
        this.setOut('length', dash);
        if (dropLabel) dropLabel.textContent = this.t.dropLabelEmpty;
        if (warning) warning.hidden = true;
        if (note) note.textContent = '';
        if (link) link.hidden = true;
        if (linkNote) linkNote.textContent = '';
        if (contact) contact.hidden = true;
        if (this.echo) this.echo.textContent = '';
        return;
      }

      if (empty) empty.hidden = true;
      if (filled) filled.removeAttribute('data-inactive');

      var currentText = this.formatNumber(result.current, 1) + '\u00a0A';
      this.setOut('current', currentText);
      this.setOut('required', this.formatArea(result.requiredArea) + '\u00a0mm²');
      this.setOut(
        'length',
        fill(this.t.lengthTotal, {
          total: this.formatNumber(result.roundTripLength, 1),
          length: this.formatNumber(result.length, 1)
        })
      );

      if (result.tooLarge) {
        this.setOut('size', this.t.tooLargeSize);
        this.setOut('awg', dash);
        this.setOut('drop', dash);
        this.setOut('ampacity', dash);
        if (dropLabel) dropLabel.textContent = this.t.dropLabelEmpty;
        var message = result.tooLargeReason === 'drop' ? this.t.tooLargeDrop : this.t.tooLargeCurrent;
        var alternatives = this.alternativesText(input);
        if (alternatives) message += ' ' + alternatives;
        if (warning) {
          warning.textContent = message;
          warning.hidden = false;
        }
        if (note) note.textContent = '';
        if (link) link.hidden = true;
        if (linkNote) linkNote.textContent = '';
        if (contact) contact.hidden = false;
        if (this.echo) this.echo.textContent = fill(this.t.echo, { size: this.t.tooLargeSize });
        this.announcement = this.t.tooLargeSize + '. ' + message;
        return;
      }

      var sizeText = this.formatSize(result.recommendedSize) + '\u00a0mm²';
      this.setOut('size', sizeText);
      this.setOut(
        'awg',
        result.awg
          ? fill(this.t.awgValue, { awg: result.awg.awg, area: this.formatArea(result.awg.area) })
          : this.t.awgTooLarge
      );
      if (dropLabel) dropLabel.textContent = fill(this.t.dropLabel, { size: sizeText });
      this.setOut(
        'drop',
        this.formatNumber(result.actualDrop, 2) + '\u00a0V (' + this.formatNumber(result.actualDropPercent, 1) + '\u00a0%)'
      );
      this.setOut(
        'ampacity',
        fill(this.t.ampacityValue, {
          ampacity: this.formatNumber(result.ampacity, 1),
          required: this.formatNumber(result.requiredCurrent, 1)
        })
      );

      var notes = [];
      if (result.governedBy === 'ampacity') {
        notes.push(fill(this.t.governedByAmpacity, { size: this.formatSize(result.dropSize) + '\u00a0mm²' }));
      }
      if (result.hot) notes.push(this.t.hotNote);
      if (result.recommendedSize < 1) notes.push(this.t.smallSizeNote);
      if (result.heavyByDrop) {
        var alternativesHeavy = this.alternativesText(input);
        notes.push(this.t.heavyTip + (alternativesHeavy ? ' ' + alternativesHeavy : ''));
      }
      if (note) note.textContent = notes.join(' ');
      if (warning) warning.hidden = true;
      if (contact) contact.hidden = true;
      if (this.echo) this.echo.textContent = fill(this.t.echo, { size: sizeText });

      this.configureProductLink(result.recommendedSize, link, linkNote);

      this.announcement = fill(this.t.announce, {
        size: sizeText,
        current: currentText,
        percent: this.formatNumber(result.actualDropPercent, 1)
      });
    }

    configureProductLink(size, link, linkNote) {
      if (!link) return;
      var sizes = Object.keys(this.productMap)
        .map(Number)
        .filter(function (value) { return Number.isFinite(value) && value >= size - EPS; })
        .sort(function (a, b) { return a - b; });

      if (!sizes.length) {
        link.hidden = true;
        if (linkNote) linkNote.textContent = '';
        return;
      }

      var target = sizes[0];
      var label = this.formatSize(target);
      link.href = this.productMap[String(target)];
      link.textContent = fill(this.t.productLink, { size: label });
      link.hidden = false;
      if (linkNote) {
        linkNote.textContent =
          target > size + EPS ? fill(this.t.productNextSize, { size: this.formatSize(size), next: label }) : '';
      }
    }

    scheduleAnnouncement(now) {
      if (!this.live) return;
      var element = this;
      root.clearTimeout(this.announceTimer);
      this.announceTimer = root.setTimeout(
        function () {
          var text = element.announcement || '';
          if (text && text !== element.lastAnnouncement) {
            element.live.textContent = text;
            element.lastAnnouncement = text;
          }
        },
        now ? 0 : 900
      );
    }

    formatSize(value) {
      return this.formatNumber(value, Number.isInteger(value) ? 0 : 2);
    }

    formatArea(value) {
      return this.formatNumber(value, value < 10 ? 2 : 1);
    }

    formatInputNumber(value) {
      return new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: false
      }).format(Math.round(value * 100) / 100);
    }

    formatNumber(value, maximumFractionDigits) {
      return new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: maximumFractionDigits
      }).format(value);
    }
  }

  customElements.define('pv-cable-size-calculator', PVCableSizeCalculatorElement);
})(typeof window !== 'undefined' ? window : globalThis);
